import { Link, useNavigate } from '@faceup/router'
import { ArrowGoBack } from '@faceup/ui'
import { Button, Col, Modal, Row, Skeleton } from '@faceup/ui-base'
import { ChannelType } from '@faceup/utils'
import { useContext, useState } from 'react'
import PageTitle from '../../Components/PageTitle'
import { LayoutContext } from '../../Contexts/LayoutContext'
import { UserContext } from '../../Contexts/UserContext'
import { useSavedReportsContext } from '../../hooks/useSavedReportsContext'
import { defineMessages, FormattedMessage, useIntl } from '../../TypedIntl'
import { sizeSettings } from '../../utils/constants'
import CheckPinForm from './CheckPinForm'
import { useCheckReportLogin } from './useCheckReportLogin'

const messages = defineMessages({
  forgottenPin: 'FollowUp.CheckReport.forgottenPin',
  forgottenPinTitle: 'FollowUp.CheckReport.forgottenPin.title',
  forgottenPinDescription: 'FollowUp.CheckReport.forgottenPin.description',
})

const titlesPerChannel = defineMessages({
  [ChannelType.AutomatedHotline]: 'FollowUp.CheckReport.title',
  [ChannelType.LiveHotline]: 'FollowUp.CheckReport.title',
  [ChannelType.AiHotline]: 'FollowUp.CheckReport.title',
  [ChannelType.ReportingSystem]: 'FollowUp.CheckReport.title',
})

const descriptionPerChannel = defineMessages({
  [ChannelType.AutomatedHotline]: 'FollowUp.CheckReport.description',
  [ChannelType.LiveHotline]: 'FollowUp.CheckReport.description',
  [ChannelType.AiHotline]: 'FollowUp.CheckReport.description',
  [ChannelType.ReportingSystem]: 'FollowUp.CheckReport.description',
})

const pinLabelPerChannel = defineMessages({
  [ChannelType.AutomatedHotline]: 'FollowUp.CheckReport.pinLabel',
  [ChannelType.LiveHotline]: 'FollowUp.CheckReport.pinLabel',
  [ChannelType.AiHotline]: 'FollowUp.CheckReport.pinLabel',
  [ChannelType.ReportingSystem]: 'FollowUp.CheckReport.pinLabel',
})

const pinErrorPerChannel = defineMessages({
  [ChannelType.AutomatedHotline]: 'FollowUp.CheckReport.pinError',
  [ChannelType.LiveHotline]: 'FollowUp.CheckReport.pinError',
  [ChannelType.AiHotline]: 'FollowUp.CheckReport.pinError',
  [ChannelType.ReportingSystem]: 'FollowUp.CheckReport.pinError',
})

const pinHintPerChannel = defineMessages({
  [ChannelType.AutomatedHotline]: 'FollowUp.CheckReport.pinHint',
  [ChannelType.LiveHotline]: 'FollowUp.CheckReport.pinHint',
  [ChannelType.AiHotline]: 'FollowUp.CheckReport.pinHint',
  [ChannelType.ReportingSystem]: 'FollowUp.CheckReport.pinHint',
})

const CheckReport = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { hasSomeSavedReports } = useSavedReportsContext()
  const { client } = useContext(LayoutContext)
  const [forgottenPinModalOpen, setForgottenPinModalOpen] = useState(false)
  const { reportSource } = useContext(UserContext)

  const { pinInput, setPinInput, loading, onSubmit } = useCheckReportLogin({
    onSuccess: () => navigate(routes => routes.report()),
  })

  const isApp = client === 'app'

  if (!reportSource) {
    return <Skeleton />
  }

  const channelType = reportSource.channelType
  if (channelType === ChannelType.Survey) {
    throw new Error('CheckReport should not be rendered for surveys')
  }

  return (
    <>
      <Row justify='center'>
        <Col {...sizeSettings}>
          {isApp && hasSomeSavedReports && (
            <div className='absolute'>
              <Link to={routes => routes.checkReport()}>
                <ArrowGoBack />
              </Link>
            </div>
          )}
          <PageTitle
            className='mb-10'
            description={<FormattedMessage {...descriptionPerChannel[channelType]} />}
          >
            <FormattedMessage {...titlesPerChannel[channelType]} />
          </PageTitle>
        </Col>
      </Row>
      <Row justify='center'>
        <Col {...sizeSettings}>
          <CheckPinForm
            pinInput={pinInput}
            setPinInput={setPinInput}
            loading={loading}
            onSubmit={onSubmit}
            label={<FormattedMessage {...pinLabelPerChannel[channelType]} />}
            hint={formatMessage(pinHintPerChannel[channelType])}
            errorMessage={<FormattedMessage {...pinErrorPerChannel[channelType]} />}
          />
          <div className='flex justify-center'>
            <Button type='link' onClick={() => setForgottenPinModalOpen(true)}>
              <FormattedMessage {...messages.forgottenPin} />
            </Button>
          </div>
        </Col>
        <Modal
          open={forgottenPinModalOpen}
          title={<FormattedMessage {...messages.forgottenPinTitle} />}
          onClose={() => setForgottenPinModalOpen(false)}
        >
          <FormattedMessage
            {...messages.forgottenPinDescription}
            values={{
              i: text => <i>{text}</i>,
            }}
          />
        </Modal>
      </Row>
    </>
  )
}

export default CheckReport
